import {useEffect, useRef, useState} from "react";
import {Bell, BellRing, X} from "lucide-react";
import {apiGet, apiPost} from "@/lib/api";

const b64ToUint8 = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
};

export async function enablePush(notify) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    notify("Phone alerts need the installed app on iPhone, or Chrome/Edge/Firefox"); return false;
  }
  const perm = await Notification.requestPermission();
  if (perm !== "granted") { notify("Alerts are blocked — allow notifications in your browser settings"); return false; }
  const reg = await navigator.serviceWorker.ready;
  const {key} = await apiGet("/push/public-key");
  const sub = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: b64ToUint8(key)});
  await apiPost("/push/subscribe", sub.toJSON());
  notify("Phone alerts are on");
  return true;
}

const pushOn = () => typeof Notification !== "undefined" && Notification.permission === "granted";

export default function NotificationBell({user, onOpen, notify}) {
  const [data, setData] = useState({items: [], unread: 0});
  const [open, setOpen] = useState(false);
  const [alertsOn, setAlertsOn] = useState(pushOn);
  const lastUnread = useRef(0);

  useEffect(() => {
    if (!user) return;
    const load = () => apiGet("/notifications").then(d => {
      if (d.unread > lastUnread.current && lastUnread.current !== 0) notify(d.items[0]?.title || "New notification");
      lastUnread.current = d.unread; setData(d);
    }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (user && pushOn()) enablePush(() => {}).catch(() => {}); }, [user]);

  const toggle = () => {
    setOpen(o => !o);
    if (!open && data.unread) apiPost("/notifications/read", {}).then(() => { lastUnread.current = 0; setData(d => ({...d, unread: 0})); }).catch(() => {});
  };
  const go = (n) => {
    setOpen(false);
    const q = new URLSearchParams((n.url || "/").split("?")[1] || "");
    onOpen(q.get("page") || "home", q.get("focus"));
  };

  return (
    <div className="bell-wrap">
      <button data-testid="notifications-button" className="icon-button" onClick={toggle}>
        <Bell size={19}/>{data.unread > 0 && <b className="bell-count" data-testid="notifications-unread">{data.unread > 9 ? "9+" : data.unread}</b>}
      </button>
      {open && (
        <div className="bell-panel" data-testid="notifications-panel">
          <div className="bell-head">
            <strong>Notifications</strong>
            <button className="icon-button" data-testid="notifications-close" onClick={() => setOpen(false)}><X size={15}/></button>
          </div>
          {!alertsOn && (
            <button data-testid="enable-push" className="coral-button sm full" onClick={async () => setAlertsOn(await enablePush(notify).catch(() => false))}>
              <BellRing size={14}/> Turn on phone alerts
            </button>
          )}
          <div className="bell-list">
            {data.items.length === 0 && <p className="admin-help">You are all caught up.</p>}
            {data.items.map((n, i) => (
              <button key={i} className={n.read ? "bell-item" : "bell-item unread"} data-testid={`notification-${i}`} onClick={() => go(n)}>
                <strong>{n.title}</strong><span>{n.body}</span><small>{new Date(n.created_at).toLocaleString()}</small>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
